import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TrendingDown } from "lucide-react";

export const LOSS_REASONS = ["Retrabalho", "Garantia", "Peça quebrada", "Outro"];
const inCls = "bg-zinc-900 border-zinc-800";
const todayIso = () => new Date().toLocaleDateString("sv-SE");

export function LossDialog({ open, loss, orderId, defaultMechanic, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [mechs, setMechs] = useState([]);
  useEffect(() => {
    if (!open) { return; }
    api.get("/employees").then(({ data }) => setMechs((data || []).filter((e) => (e.role || "mecanico") === "mecanico").map((e) => e.name))).catch(() => setMechs([]));
  }, [open]);
  useEffect(() => {
    if (!open) { return; }
    setF(loss ? { date: loss.date, amount: String(loss.amount), reason: loss.reason, reason_text: loss.reason_text || "", order_number: loss.order_number ?? "", note: loss.note || "", mechanic_name: loss.mechanic_name || "" }
      : { date: todayIso(), amount: "", reason: "", reason_text: "", order_number: "", note: "", mechanic_name: defaultMechanic || "" });
  }, [open, loss, defaultMechanic]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      let oid = loss ? loss.order_id : orderId || null;
      if (!orderId && String(f.order_number || "").trim() && String(f.order_number) !== String(loss?.order_number ?? "")) {
        const { data } = await api.get("/orders");
        const o = (data || []).find((x) => String(x.order_number) === String(f.order_number).trim());
        if (!o) { toast.error("OS não encontrada com esse número"); setBusy(false); return; }
        oid = o.id;
      } else if (!orderId && !String(f.order_number || "").trim()) { oid = null; }
      const body = { date: f.date, amount: Number(String(f.amount).replace(",", ".")), reason: f.reason, reason_text: f.reason === "Outro" ? f.reason_text : null, order_id: oid, note: f.note, mechanic_name: f.mechanic_name || null };
      loss ? await api.put(`/losses/${loss.id}`, body) : await api.post("/losses", body);
      toast.success(loss ? "Prejuízo atualizado" : "Prejuízo lançado"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const ok = f.date && Number(String(f.amount || "").replace(",", ".")) > 0 && f.reason && (f.reason !== "Outro" || (f.reason_text || "").trim());
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent data-testid="loss-dialog" className="bg-zinc-950 border-zinc-800">
        <DialogHeader><DialogTitle>{loss ? "Editar prejuízo" : "Lançar prejuízo"}</DialogTitle><DialogDescription>Não altera o valor da OS, pagamentos, caixa nem comissões.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[11px] text-zinc-400">Data<Input data-testid="loss-date" type="date" value={f.date || ""} onChange={set("date")} className={`mt-1 ${inCls}`} /></label>
            <label className="text-[11px] text-zinc-400">Valor (R$)<Input data-testid="loss-amount" inputMode="decimal" value={f.amount || ""} onChange={set("amount")} placeholder="0,00" className={`mt-1 ${inCls}`} /></label>
          </div>
          <div className="text-[11px] text-zinc-400">Motivo</div>
          <div className="flex flex-wrap gap-2">{LOSS_REASONS.map((r, i) => (
            <button key={r} type="button" data-testid={`loss-reason-${i}`} onClick={() => setF({ ...f, reason: r })} className={`h-9 px-3 rounded-md border text-sm ${f.reason === r ? "border-amber-brand text-amber-200 bg-amber-brand/10" : "border-zinc-700 text-zinc-300"}`}>{r}</button>
          ))}</div>
          {f.reason === "Outro" && <Input data-testid="loss-reason-text" value={f.reason_text || ""} onChange={set("reason_text")} placeholder="Descreva o motivo *" className={inCls} />}
          {!orderId && <label className="block text-[11px] text-zinc-400">OS vinculada (nº, opcional)<Input data-testid="loss-order-number" value={f.order_number ?? ""} onChange={set("order_number")} placeholder="Ex.: 1023" className={`mt-1 ${inCls}`} /></label>}
          <label className="block text-[11px] text-zinc-400">Mecânico responsável (opcional)
            <select data-testid="loss-mechanic" value={f.mechanic_name || ""} onChange={set("mechanic_name")} className="mt-1 w-full h-10 rounded-md bg-zinc-900 border border-zinc-800 px-2 text-sm text-zinc-100">
              <option value="">— sem mecânico —</option>
              {[...new Set([...mechs, ...(f.mechanic_name ? [f.mechanic_name] : [])])].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <Textarea data-testid="loss-note" rows={2} value={f.note || ""} onChange={set("note")} placeholder="Observação" className={inCls} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} className={inCls}>Cancelar</Button>
            <Button data-testid="loss-save" disabled={busy || !ok} onClick={save} className="bg-red-700 hover:bg-red-600 text-white font-bold">Salvar</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function LossButton({ order }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button data-testid="order-loss-btn" variant="outline" size="sm" onClick={() => setOpen(true)} className="border-red-800 text-red-300 bg-transparent hover:bg-red-950/40"><TrendingDown className="h-4 w-4 mr-1" /> Lançar prejuízo</Button>
      <LossDialog open={open} orderId={order.id} defaultMechanic={order.mechanic_name || ""} onClose={() => setOpen(false)} onSaved={() => setOpen(false)} />
    </>
  );
}
